import type {
  DatabricksAuthType,
  DiscoveredTable,
  MysqlSslMode,
  SqlServerAuthType,
} from '../services/admin-api';
import type { SslMode } from './connection-labels';

/**
 * Validaciones de formulario para guiar al usuario junto a cada campo. El servidor
 * vuelve a validarlo todo: estas reglas solo mejoran la experiencia.
 */

/** Mismo minimo que exige el servidor. */
export const PASSWORD_MIN_LENGTH = 10;

export interface PgForm {
  readonly host: string;
  readonly port: string;
  readonly database: string;
  readonly schema: string;
  readonly user: string;
  readonly password: string;
  readonly sslMode: SslMode;
}

const HOST_PATTERN = /^[A-Za-z0-9.\-_:[\]]+$/;

export function pgErrors(form: PgForm): Partial<Record<keyof PgForm, string>> {
  const errors: Partial<Record<keyof PgForm, string>> = {};
  if (form.host.trim() === '') errors.host = 'Indique el servidor (nombre o direccion IP).';
  else if (!HOST_PATTERN.test(form.host.trim())) {
    errors.host = 'Solo el nombre o la IP, sin http:// ni rutas.';
  }
  const port = Number(form.port);
  if (!Number.isInteger(port) || port < 1 || port > 65535) errors.port = 'Puerto entre 1 y 65535.';
  if (form.database.trim() === '') errors.database = 'Indique la base de datos.';
  if (form.user.trim() === '') errors.user = 'Indique el usuario.';
  return errors;
}

export interface DatabricksForm {
  readonly host: string;
  readonly httpPath: string;
  readonly catalog: string;
  readonly schema: string;
  readonly authType: DatabricksAuthType;
  readonly clientId: string;
  readonly secret: string;
}

const DBX_HOST_PATTERN = /^[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)+$/;
const DBX_PATH_PATTERN = /^\/sql\/1\.0\/(warehouses|endpoints)\/[A-Za-z0-9]+$/;

/** Igual que el servidor: acepta lo copiado del workspace (`https://host/`, `sql/1.0/...`). */
export const normalizeDbxHost = (raw: string): string =>
  raw
    .trim()
    .replace(/^https?:\/\//i, '')
    .replace(/\/+$/, '');
export const normalizeDbxPath = (raw: string): string => {
  const path = raw.trim();
  return path === '' || path.startsWith('/') ? path : `/${path}`;
};

/**
 * `secretRequired`: al crear, o al editar si no hay secreto guardado o cambian el
 * servidor o la identidad (el servidor no reutiliza el secreto en esos casos).
 */
export function databricksErrors(
  form: DatabricksForm,
  secretRequired: boolean,
): Partial<Record<keyof DatabricksForm, string>> {
  const errors: Partial<Record<keyof DatabricksForm, string>> = {};
  const host = normalizeDbxHost(form.host);
  if (host === '') errors.host = 'Indique el Server hostname del workspace.';
  else if (!DBX_HOST_PATTERN.test(host) || /^\d+(\.\d+){3}$/.test(host)) {
    errors.host = 'Solo el nombre del servidor, por ejemplo adb-123.4.azuredatabricks.net.';
  }
  const path = normalizeDbxPath(form.httpPath);
  if (path === '') errors.httpPath = 'Indique el HTTP Path del SQL Warehouse.';
  else if (!DBX_PATH_PATTERN.test(path)) {
    errors.httpPath = 'Use el HTTP Path del SQL Warehouse: /sql/1.0/warehouses/<id>.';
  }
  if (form.authType === 'oauth-m2m' && form.clientId.trim() === '') {
    errors.clientId = 'Indique el Client ID (Application ID) del service principal.';
  }
  if (secretRequired && form.secret === '') {
    errors.secret =
      form.authType === 'pat'
        ? 'Indique el token de acceso.'
        : 'Indique el secreto del service principal.';
  }
  return errors;
}

/** Igual que el servidor: acepta `tcp:srv.database.windows.net,1433` o `https://srv/`. */
export const normalizeSqlHost = (raw: string): string =>
  raw
    .trim()
    .replace(/^(?:tcp:|https?:\/\/)/i, '')
    .replace(/[,:]\d+$/, '')
    .replace(/\/+$/, '');

const SQL_HOST_PATTERN =
  /^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*$/;
const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const TENANT_PATTERN = /^(?:[0-9a-f-]{36}|[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+)$/i;

function serverErrors(host: string, port: string): { host?: string; port?: string } {
  const errors: { host?: string; port?: string } = {};
  const normalized = normalizeSqlHost(host);
  if (normalized === '') errors.host = 'Indique el servidor (nombre o direccion IP).';
  else if (!SQL_HOST_PATTERN.test(normalized)) {
    errors.host = 'Solo el nombre o la IP del servidor, sin rutas.';
  }
  const value = Number(port);
  if (!Number.isInteger(value) || value < 1 || value > 65535)
    errors.port = 'Puerto entre 1 y 65535.';
  return errors;
}

export interface SqlServerForm {
  readonly host: string;
  readonly port: string;
  readonly database: string;
  readonly schema: string;
  readonly authType: SqlServerAuthType;
  readonly user: string;
  readonly tenantId: string;
  readonly clientId: string;
  readonly encrypt: boolean;
  readonly trustServerCertificate: boolean;
  readonly secret: string;
}

export function sqlServerErrors(
  form: SqlServerForm,
  secretRequired: boolean,
): Partial<Record<keyof SqlServerForm, string>> {
  const errors: Partial<Record<keyof SqlServerForm, string>> = serverErrors(form.host, form.port);
  if (form.database.trim() === '') errors.database = 'Indique la base de datos.';
  if (form.authType === 'sql') {
    if (form.user.trim() === '') errors.user = 'Indique el usuario SQL.';
  } else {
    if (!TENANT_PATTERN.test(form.tenantId.trim())) {
      errors.tenantId = 'Indique el Directory (tenant) ID de Entra ID.';
    }
    if (!GUID_PATTERN.test(form.clientId.trim())) {
      errors.clientId = 'Indique el Application (client) ID del service principal.';
    }
  }
  if (secretRequired && form.secret === '') {
    errors.secret =
      form.authType === 'sql'
        ? 'Indique la contrasena.'
        : 'Indique el secreto del service principal.';
  }
  return errors;
}

export interface MysqlForm {
  readonly host: string;
  readonly port: string;
  readonly database: string;
  readonly user: string;
  readonly password: string;
  readonly sslMode: MysqlSslMode;
}

export function mysqlErrors(
  form: MysqlForm,
  secretRequired: boolean,
): Partial<Record<keyof MysqlForm, string>> {
  const errors: Partial<Record<keyof MysqlForm, string>> = serverErrors(form.host, form.port);
  if (form.database.trim() === '') errors.database = 'Indique la base de datos.';
  if (form.user.trim() === '') errors.user = 'Indique el usuario.';
  if (secretRequired && form.password === '') errors.password = 'Indique la contrasena.';
  return errors;
}

const AZURE_AI_HOST =
  /\.(?:services\.ai|cognitiveservices|openai|models\.ai|inference\.ai)\.azure\.com$/i;
const AZURE_SERVERLESS_HOST = /\.(?:models|inference)\.ai\.azure\.com$/i;
const AZURE_MODEL = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;

/** Host del endpoint de Azure AI o `null` si no es una URL https de Azure AI. */
export function azureEndpointHost(raw: string): string | null {
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'https:' && AZURE_AI_HOST.test(url.hostname)
      ? url.hostname.toLowerCase()
      : null;
  } catch {
    return null;
  }
}

export const isServerlessHost = (host: string | null): boolean =>
  host !== null && AZURE_SERVERLESS_HOST.test(host);

export type AzureApi = 'chat-completions' | 'anthropic-messages';

/** Igual que el servidor: Target URI de Claude (`/anthropic/...`) o despliegue `claude*`. */
export function inferAzureApi(endpoint: string, model: string): AzureApi {
  let path = '';
  try {
    path = new URL(endpoint.trim()).pathname;
  } catch {
    // Endpoint incompleto mientras se escribe.
  }
  return /^\/anthropic(?:\/|$)/i.test(path) || /^claude/i.test(model.trim())
    ? 'anthropic-messages'
    : 'chat-completions';
}

export interface AzureForm {
  readonly endpoint: string;
  readonly model: string;
  readonly api: AzureApi;
  readonly authType: 'api-key' | 'entra-sp';
  readonly tenantId: string;
  readonly clientId: string;
  readonly secret: string;
}

/**
 * Reglas del formulario de Azure AI Foundry (el servidor repite todas). `host` es el del
 * endpoint efectivo: el escrito o, si no se reemplaza, el guardado.
 */
export function azureErrors(
  form: AzureForm,
  options: {
    readonly replaceEndpoint: boolean;
    readonly host: string | null;
    readonly secretRequired: boolean;
  },
): Partial<Record<keyof AzureForm, string>> {
  const errors: Partial<Record<keyof AzureForm, string>> = {};
  if (options.replaceEndpoint) {
    if (form.endpoint.trim() === '') errors.endpoint = 'Pegue el endpoint de Azure AI Foundry.';
    else if (options.host === null) {
      errors.endpoint =
        'Use la URL https del recurso, por ejemplo https://mi-recurso.services.ai.azure.com.';
    }
  }
  const model = form.model.trim();
  if (model === '' && !isServerlessHost(options.host)) {
    errors.model = 'Indique el nombre del despliegue del modelo.';
  } else if (model !== '' && !AZURE_MODEL.test(model)) {
    errors.model = 'Use el nombre del despliegue tal como aparece en Azure AI Foundry.';
  }
  if (form.api === 'anthropic-messages' && isServerlessHost(options.host)) {
    errors.api = 'Claude se usa desde el recurso de Foundry (*.services.ai.azure.com).';
  }
  if (form.authType === 'entra-sp') {
    if (!TENANT_PATTERN.test(form.tenantId.trim())) {
      errors.tenantId = 'Indique el Directory (tenant) ID de Entra ID.';
    }
    if (!GUID_PATTERN.test(form.clientId.trim())) {
      errors.clientId = 'Indique el Application (client) ID del service principal.';
    }
  }
  if (options.secretRequired && form.secret === '') {
    errors.secret =
      form.authType === 'api-key'
        ? 'Indique la clave de API del recurso.'
        : 'Indique el secreto del service principal.';
  }
  return errors;
}

/** Regla basica de la URL de n8n (el servidor valida ademas https y link-local). */
export function webhookUrlError(raw: string): string | null {
  if (raw.trim() === '') return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return 'Escriba una URL completa, por ejemplo https://n8n.empresa.com/webhook/aibi.';
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'Use una URL http o https.';
  if (url.username !== '' || url.password !== '') {
    return 'No incluya usuario ni contrasena en la URL; use el campo token.';
  }
  return null;
}

/** Solo se pueden modelar tablas exploradas completa o parcialmente. */
export const isSelectable = (table: DiscoveredTable): boolean =>
  table.status === 'ok' || table.status === 'partial';
